import IconChevronLeft16 from '@warp-ds/icons/react/chevron-left-16';
import IconChevronRight16 from '@warp-ds/icons/react/chevron-right-16';
import { Button } from '@warp-ds/react';
import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import type { Agent, DeviceType } from '../agent';
import { trackScrollList } from '../utils/tracking';
import { MeglerpromoCard } from './MeglerpromoCard';
import { PristipsCard } from './PristipsCard';
import { SoldMapCard } from './SoldMapCard';

interface Props {
    agents: Agent[];
    squareMeterPrice: number | null;
    deviceType: DeviceType;
    searchParams: URLSearchParams;
}

interface ArrowProps {
    label: string;
    disabled: boolean;
    onClick: () => void;
    children: ReactNode;
}

const closestPage = (pageOffsets: number[], scrollLeft: number): number =>
    pageOffsets.reduce(
        (closest, offset, index) =>
            Math.abs(offset - scrollLeft) < Math.abs((pageOffsets[closest] ?? 0) - scrollLeft) ? index : closest,
        0,
    );

const getPageOffsets = (viewport: HTMLElement, cardsPerPage: number, cardCount: number): number[] => {
    const cards = Array.from(viewport.querySelectorAll<HTMLElement>('.meglerpromo-card'));
    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    if (cards.length === 0 || maxScroll === 0) return [0];

    const firstCardOffset = cards[0]?.offsetLeft ?? 0;
    const offsets = Array.from({ length: Math.ceil(cardCount / cardsPerPage) }, (_, page) => {
        const firstCardOnPage = cards[page * cardsPerPage];
        return Math.min(maxScroll, Math.max(0, (firstCardOnPage?.offsetLeft ?? maxScroll) - firstCardOffset));
    });

    return offsets.filter((offset, index) => index === 0 || offset !== offsets[index - 1]);
};

const CarouselArrow: FC<ArrowProps> = ({ label, disabled, onClick, children }) => (
    <Button
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        small
        utility
        quiet
        className="meglerpromo-carousel-arrow"
    >
        {children}
    </Button>
);

export const MeglerpromoCarousel: FC<Props> = ({ agents, squareMeterPrice, deviceType, searchParams }) => {
    const cardsPerPage = deviceType === 'desktop' ? 3 : 2;
    const cardCount = agents.length + 2;
    const estimatedPageCount = Math.ceil(cardCount / cardsPerPage);
    const viewportRef = useRef<HTMLElement>(null);
    const arrowTargetPageRef = useRef<number | null>(null);
    const [page, setPage] = useState(0);
    const [pageOffsets, setPageOffsets] = useState<number[]>([]);
    const [canGoToPrevious, setCanGoToPrevious] = useState(false);
    const [canGoToNext, setCanGoToNext] = useState(estimatedPageCount > 1);
    const isDesktop = deviceType === 'desktop';
    const pageCount = pageOffsets.length || estimatedPageCount;

    const syncNavigation = (offsets = pageOffsets) => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
        const arrowTargetPage = arrowTargetPageRef.current;
        if (arrowTargetPage === null) {
            setPage(closestPage(offsets, viewport.scrollLeft));
        } else {
            setPage(arrowTargetPage);
            if (Math.abs((offsets[arrowTargetPage] ?? 0) - viewport.scrollLeft) <= 1) {
                arrowTargetPageRef.current = null;
            }
        }
        setCanGoToPrevious(viewport.scrollLeft > 1);
        setCanGoToNext(viewport.scrollLeft < maxScroll - 1);
    };

    useEffect(() => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        const updateLayout = () => {
            const offsets = getPageOffsets(viewport, cardsPerPage, cardCount);
            const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
            arrowTargetPageRef.current = null;
            setPageOffsets(offsets);
            setPage(closestPage(offsets, viewport.scrollLeft));
            setCanGoToPrevious(viewport.scrollLeft > 1);
            setCanGoToNext(viewport.scrollLeft < maxScroll - 1);
        };

        updateLayout();
        const cancelArrowNavigation = () => {
            arrowTargetPageRef.current = null;
        };
        const resizeObserver = new ResizeObserver(updateLayout);
        resizeObserver.observe(viewport);
        viewport.addEventListener('wheel', cancelArrowNavigation, {
            passive: true,
        });
        viewport.addEventListener('pointerdown', cancelArrowNavigation, {
            passive: true,
        });
        window.addEventListener('resize', updateLayout);
        return () => {
            resizeObserver.disconnect();
            viewport.removeEventListener('wheel', cancelArrowNavigation);
            viewport.removeEventListener('pointerdown', cancelArrowNavigation);
            window.removeEventListener('resize', updateLayout);
        };
    }, [cardsPerPage, cardCount]);

    const goTo = (direction: -1 | 1) => {
        const viewport = viewportRef.current;
        if (!viewport) return;

        trackScrollList();
        const currentPage = arrowTargetPageRef.current ?? closestPage(pageOffsets, viewport.scrollLeft);
        const targetPage = Math.max(0, Math.min(pageOffsets.length - 1, currentPage + direction));
        const target = pageOffsets[targetPage] ?? 0;

        arrowTargetPageRef.current = targetPage;
        setPage(targetPage);
        viewport.scrollTo({ left: target, behavior: 'smooth' });
    };

    return (
        <>
            <section
                ref={viewportRef}
                className="meglerpromo-carousel-viewport overflow-x-auto overflow-y-clip py-4 px-24"
                onScroll={() => syncNavigation()}
                aria-label="Eiendomsmeglere og boligtjenester"
                aria-roledescription="karusell"
            >
                <div className="meglerpromo-track flex gap-16">
                    {agents.map((agent, i) => (
                        <MeglerpromoCard key={agent.profileUrl} agent={agent} position={i} />
                    ))}
                    <PristipsCard squareMeterPrice={squareMeterPrice} deviceType={deviceType} />
                    <SoldMapCard deviceType={deviceType} searchParams={searchParams} />
                </div>
            </section>

            {isDesktop && pageCount > 1 && (
                <div className="flex items-center justify-center gap-16 pt-8">
                    <CarouselArrow label="Forrige" disabled={!canGoToPrevious} onClick={() => goTo(-1)}>
                        <IconChevronLeft16 aria-hidden="true" />
                    </CarouselArrow>
                    <w-page-indicator selected-page={page + 1} page-count={pageCount} />
                    <CarouselArrow label="Neste" disabled={!canGoToNext} onClick={() => goTo(1)}>
                        <IconChevronRight16 aria-hidden="true" />
                    </CarouselArrow>
                </div>
            )}
        </>
    );
};
